// DependentParentSelector.tsx
import React, { useMemo, useState, useEffect } from "react";
import Select from "react-select";
import { Form, Button } from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";
import { FieldProps } from "../../../../../../../InputFieldsController/InputFieldsController.utils";

/**
 * Props for this component.
 * onChange expects a partial patch that will be merged into the selected field by the parent configurator.
 */
export interface DependentParentSelectorProps {
  selectedField: FieldProps;
  fieldOptions: FieldProps[]; // full FieldProps so we can read .options when available
  onChange: (patch: Partial<FieldProps>) => void;
}

/**
 * DependentParentSelector
 * - Uses parent's .options when available
 * - Otherwise allows manual values (single value input, add + remove)
 * - Emits dependsOnVisibilityValues (array) and dependsOnVisibilityValue (legacy single)
 */
const DependentParentSelector: React.FC<DependentParentSelectorProps> = ({
  selectedField,
  fieldOptions,
  onChange,
}) => {
  // list of other fields to choose from as parent
  const fieldListOptions = useMemo(
    () =>
      (fieldOptions ?? [])
        .filter((f) => f.code !== selectedField.code)
        .map((f) => ({ label: f.label, value: f.code ?? "" })),
    [fieldOptions, selectedField.code]
  );

  // parent id/value selected
  const selectedParent = selectedField.dependsOn
    ? {
        label:
          fieldOptions?.find((f) => f.code === selectedField.dependsOn)?.label ??
          "",
        value: selectedField.dependsOn,
      }
    : null;

  // find the parent field object if selected
  const parentField = useMemo(
    () => fieldOptions?.find((f) => f.code === selectedField.dependsOn),
    [fieldOptions, selectedField.dependsOn]
  );

  // parent-provided choices if present
  const parentOptionChoices =
    parentField?.options?.map((o: any) => ({
      label: String(o.value),
      value: o.value,
    })) ?? [];

  // Manual values stored locally. Initialize from incoming selectedField values for UX continuity.
  const initialManualValues = useMemo(() => {
    if (
      Array.isArray(selectedField.dependsOnVisibilityValues) &&
      selectedField.dependsOnVisibilityValues.length
    )
      return [...selectedField.dependsOnVisibilityValues];
    if (
      selectedField.dependsOnVisibilityValue !== undefined &&
      selectedField.dependsOnVisibilityValue !== ""
    )
      return [selectedField.dependsOnVisibilityValue];
    return [];
  }, [
    selectedField.dependsOnVisibilityValues,
    selectedField.dependsOnVisibilityValue,
  ]);

  const [manualValues, setManualValues] = useState<any[]>(initialManualValues);
  const [newValueRaw, setNewValueRaw] = useState("");

  useEffect(() => {
    // keep local manual values in sync if parent updates selectedField externally
    setManualValues(initialManualValues);
  }, [initialManualValues]);

  // choices used by multi-select. prefer real parent options if present
  const multiChoices =
    parentOptionChoices.length > 0
      ? parentOptionChoices
      : manualValues.map((v) => ({ label: String(v), value: v }));

  // derive current selected react-select items
  const currentMultiValue =
    Array.isArray(selectedField.dependsOnVisibilityValues) &&
    multiChoices.length
      ? multiChoices.filter((opt) =>
          (selectedField.dependsOnVisibilityValues || []).some(
            (v) => String(v) === String(opt.value)
          )
        )
      : selectedField.dependsOnVisibilityValue && multiChoices.length
      ? multiChoices.filter(
          (opt) =>
            String(opt.value) === String(selectedField.dependsOnVisibilityValue)
        )
      : [];

  // helper to emit partial patch
  const emitValues = (vals: any[]) => {
    onChange({
      dependsOnVisibilityValues: vals,
      dependsOnVisibilityValue: vals.length === 1 ? vals[0] : "",
    });
  };

  // react-select change
  const handleMultiSelectChange = (selected: any[] | null) => {
    const vals = (selected || []).map((s) => s.value);
    emitValues(vals);
  };

  // parse simple primitives from user text input
  const tryParsePrimitive = (raw: string) => {
    const trimmed = raw.trim();
    if (/^(true|false)$/i.test(trimmed))
      return trimmed.toLowerCase() === "true";
    if (trimmed !== "" && !isNaN(Number(trimmed))) return Number(trimmed);
    return trimmed;
  };

  // add manual value
  const handleAddManualValue = () => {
    if (newValueRaw.trim() === "") return;
    const parsed = tryParsePrimitive(newValueRaw);
    if (manualValues.some((m) => String(m) === String(parsed))) {
      setNewValueRaw("");
      return;
    }
    const next = [...manualValues, parsed];
    setManualValues(next);

    // append to selected values as well
    const existing = Array.isArray(selectedField.dependsOnVisibilityValues)
      ? [...selectedField.dependsOnVisibilityValues]
      : selectedField.dependsOnVisibilityValue
      ? [selectedField.dependsOnVisibilityValue]
      : [];
    const nextVals = [...existing, parsed];
    emitValues(nextVals);

    setNewValueRaw("");
  };

  // remove manual value and remove it from selected values too
  // const handleRemoveManualValue = (valToRemove: any) => {
  //   const next = manualValues.filter((m) => String(m) !== String(valToRemove));
  //   setManualValues(next);

  //   const existing = Array.isArray(selectedField.dependsOnVisibilityValues)
  //     ? selectedField.dependsOnVisibilityValues
  //     : selectedField.dependsOnVisibilityValue
  //     ? [selectedField.dependsOnVisibilityValue]
  //     : [];

  //   const filteredSelected = existing.filter((v: any) => String(v) !== String(valToRemove));
  //   emitValues(filteredSelected);
  // };

  const handleClear = () => {
    onChange({
      dependsOn: "",
      dependsOnVisibilityValue: "",
      dependsOnVisibilityValues: [],
    });
    setManualValues([]);
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-1 mt-2">
        <label style={{ fontWeight: 500, fontSize: 8 }}>Depends On</label>
        {selectedField.dependsOn && (
          <Icon.XCircleFill
            size={16}
            style={{ color: "crimson", cursor: "pointer" }}
            onClick={handleClear}
            title="Clear dependency"
          />
        )}
      </div>

      <Select
        options={fieldListOptions}
        value={selectedParent}
        onChange={(option) => {
          onChange({
            dependsOn: option?.value || "",
            dependsOnVisibilityValue: "",
            dependsOnVisibilityValues: [],
          })
        }}
        placeholder="Select parent field"
        className="custom-select mb-2"
        classNamePrefix="react-select"
        menuPortalTarget={document.body}
        styles={{
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          menu: (base) => ({ ...base, zIndex: 9999 }),
        }}
      />

      {selectedField.dependsOn ? (
        <>
          <label style={{ fontWeight: 500, fontSize: 8 }}>
            Visible When Parent Equals
          </label>

          {/* manual editor only when parent has no real options */}
          {parentOptionChoices.length === 0 && (
            <>
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  marginBottom: 8,
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <div>
                  <Form.Control
                    size="sm"
                    placeholder="Value (e.g., true, yes, 1)"
                    value={newValueRaw}
                    onChange={(e) => setNewValueRaw(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddManualValue();
                      }
                    }}
                    className="form-control move-left custom-input"
                  />
                </div>
                <div>
                  <Icon.PlusCircleFill
                    size={25}
                    style={{
                      color: "var(--theme1_primary)",
                      cursor: "pointer",
                    }}
                    onClick={handleAddManualValue}
                    title="Add dependency value"
                  />
                </div>
              </div>

              <Select
                isMulti
                options={multiChoices}
                value={currentMultiValue}
                onChange={(vals) => handleMultiSelectChange(vals as any[])}
                className="custom-select mb-2"
                classNamePrefix="react-select"
                placeholder={
                  multiChoices.length
                    ? "Select one or more values"
                    : "No options"
                }
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  menu: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />

              {/* <div style={{ marginBottom: 6 }}>
                {manualValues.length === 0 ? (
                  <small style={{ opacity: 0.7 }}>No manual values. Add one above and press plus.</small>
                ) : (
                  manualValues.map((val) => (
                    <div
                      key={String(val)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "6px 8px",
                        borderRadius: 6,
                        border: "1px solid rgba(0,0,0,0.06)",
                        marginBottom: 6,
                        background: "#fff",
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13 }}>{String(val)}</div>
                      </div>
                      <Icon.Trash
                        size={16}
                        style={{ color: "crimson", cursor: "pointer" }}
                        onClick={() => handleRemoveManualValue(val)}
                        title="Remove value"
                      />
                    </div>
                  ))
                )}
              </div> */}
            </>
          )}

          <small style={{ opacity: 0.75 }}>
            Field will show when parent matches any of the selected values.
          </small>
        </>
      ) : null}
    </>
  );
};

export default DependentParentSelector;
